import { ObjectId } from "@novorender/api";
import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";

import { Tree } from "components/objectTree/types";
import { getParentPaths } from "components/objectTree/utils";
import { ModelTreeFilterObjects, ModelTreeNodeData } from "features/modelTree/types";
import { getDescendantsFilteredByParserTypeInfo, makeNode } from "features/modelTree/utils";
import { NodeType } from "types/misc";
import { AppObjectDB } from "utils/appObjectDb";
import { getFilePathFromObjectPath } from "utils/objectData";
import { batchedPropertySearch, iterateAsync } from "utils/search";

const MAX_SAMPLE_IDS = 200;
const MAX_CHILDREN_PER_NODE = 500;
const MAX_DEPTH_ITERATIONS = 20;

/** Max concurrent db.search calls per depth iteration. */
const MAX_CONCURRENT_LOADS = 5;

/**
 * When objects are selected in the 3D view (e.g. via lasso / area selection),
 * the object tree may not have loaded the subtree containing them.
 *
 * This hook resolves a sample of selected object IDs to their paths, computes
 * ancestor paths, and loads children for any "frontier" tree nodes (nodes
 * whose children haven't been loaded yet) that lie on those ancestor paths.
 * It iterates level by level until the full ancestor chain is present in the
 * tree, so that {@link getGroupStateForIdSet} can correctly propagate
 * selection state up to parent folders (showing "some" instead of "none").
 */
export function useLoadAncestorsForSelection(
    tree: Tree<ModelTreeNodeData>,
    db: AppObjectDB | undefined,
    selectedIdArr: ObjectId[],
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>,
    fileTypes?: ModelTreeFilterObjects[],
): void {
    const setTreeRef = useRef(setTree);
    setTreeRef.current = setTree;
    const selectedIdArrRef = useRef(selectedIdArr);
    selectedIdArrRef.current = selectedIdArr;

    useEffect(() => {
        if (!db || selectedIdArr.length === 0) {
            return;
        }

        // Quick check: any non-leaf nodes without children loaded and not
        // already being loaded by another source (e.g. selectNode)?
        // Include the root node so the hook can bootstrap from parentPath: "".
        const hasFrontierNodes = Object.values(tree).some((n) => !n.isLeaf && !n.children && !n.loading);

        // If not, the tree is fully expanded (or already loading) and there's nothing to do.
        if (!hasFrontierNodes) {
            return;
        }

        const abortController = new AbortController();
        const signal = abortController.signal;

        (async () => {
            try {
                // 1. Sample selected IDs and resolve to objects to get paths.
                //    First, resolve as many as possible from the existing tree to
                //    avoid redundant network requests (selectNode / expandOrSelectNode
                //    already loads the clicked object into the tree).
                const sampleIds = selectedIdArrRef.current.slice(0, MAX_SAMPLE_IDS);

                const resolvedObjects: { path: string }[] = [];
                const unresolvedIds: string[] = [];

                const treeNodesByIdSnapshot = new Map<number, string>();
                for (const node of Object.values(tree)) {
                    treeNodesByIdSnapshot.set(node.data.object.id, node.path);
                }

                for (const id of sampleIds) {
                    const path = treeNodesByIdSnapshot.get(id);
                    if (path !== undefined) {
                        resolvedObjects.push({ path });
                    } else {
                        unresolvedIds.push(String(id));
                    }
                }

                // Only search for IDs not already in the tree.
                if (unresolvedIds.length > 0) {
                    const searchResults = await batchedPropertySearch({
                        property: "id",
                        value: unresolvedIds,
                        db,
                        abortSignal: signal,
                    });

                    if (signal.aborted) {
                        return;
                    }

                    for (const obj of searchResults) {
                        resolvedObjects.push({ path: obj.path });
                    }
                }

                if (signal.aborted || resolvedObjects.length === 0) {
                    return;
                }

                // 2. Compute all unique ancestor paths from selected objects.
                const ancestorPaths = new Set<string>();
                for (const obj of resolvedObjects) {
                    for (const p of getParentPaths(obj.path)) {
                        ancestorPaths.add(p);
                    }
                }

                // 3. Iteratively load children for frontier nodes on ancestor
                //    paths. Each iteration goes one level deeper. We maintain a
                //    lightweight working copy to track which nodes have been
                //    loaded without mutating the real tree yet.
                type LoadResult = {
                    path: string;
                    children: HierarcicalObjectReference[];
                    childIterator?: AsyncIterableIterator<HierarcicalObjectReference>;
                };
                const allResults: LoadResult[] = [];

                const workingTree: Record<string, { children?: string[]; isLeaf: boolean }> = {};
                for (const [path, node] of Object.entries(tree)) {
                    workingTree[path] = { children: node.children, isLeaf: node.isLeaf };
                }

                for (let depth = 0; depth < MAX_DEPTH_ITERATIONS; depth++) {
                    if (signal.aborted) {
                        return;
                    }

                    // Find frontier nodes that are ancestors of selected objects.
                    // Skip nodes already being loaded by another source (e.g. selectNode)
                    // to avoid issuing duplicate network requests.
                    const frontier: string[] = [];
                    for (const path of ancestorPaths) {
                        const entry = workingTree[path];
                        if (entry && !entry.isLeaf && !entry.children) {
                            // Check the real tree for loading state set by selectNode.
                            const realNode = tree[path];
                            if (realNode?.loading) {
                                continue;
                            }
                            frontier.push(path);
                        }
                    }

                    if (frontier.length === 0) {
                        break;
                    }

                    // Load children for frontier nodes with a concurrency cap
                    // to avoid overwhelming the backend / browser connection limits.
                    const results = await pMap(
                        frontier,
                        async (path): Promise<LoadResult | null> => {
                            try {
                                // Reuse an existing iterator stored on the node (from a
                                // previous partial load) so we continue where we left off,
                                // mirroring the pattern used by loadChildren in modelTree/utils.
                                const existingNode = tree[path];
                                // Apply the same file-type filtering as loadChildren:
                                // only filter when fileTypes are active and the path is
                                // above file level.
                                const isFileOrInsideFile = getFilePathFromObjectPath(path);
                                const pathFilter =
                                    fileTypes && !isFileOrInsideFile
                                        ? [...new Set(getDescendantsFilteredByParserTypeInfo(path, fileTypes))]
                                        : [];
                                const iterator =
                                    existingNode?.data.childIterator ??
                                    db.search(
                                        {
                                            parentPath: path,
                                            descentDepth: 1,
                                            searchPattern: pathFilter.length
                                                ? [{ property: "path", value: pathFilter, exact: true }]
                                                : [],
                                        },
                                        signal,
                                    );

                                const children: HierarcicalObjectReference[] = [];
                                let done = false;

                                while (!done && !signal.aborted) {
                                    const [batch, batchDone] = await iterateAsync({
                                        iterator,
                                        count: MAX_CHILDREN_PER_NODE,
                                        abortSignal: signal,
                                    });
                                    children.push(...batch);
                                    done = batchDone;
                                }

                                return {
                                    path,
                                    children,
                                    childIterator: undefined,
                                };
                            } catch {
                                return null;
                            }
                        },
                        { concurrency: MAX_CONCURRENT_LOADS, signal },
                    );

                    if (signal.aborted) {
                        return;
                    }

                    // Apply to working tree for next iteration.
                    let anyNew = false;
                    for (const result of results) {
                        if (!result) {
                            continue;
                        }
                        const entry = workingTree[result.path];
                        if (!entry || entry.children) {
                            continue;
                        }

                        const childPaths: string[] = [];
                        for (const child of result.children) {
                            if (!workingTree[child.path]) {
                                workingTree[child.path] = {
                                    children: undefined,
                                    isLeaf: child.type === NodeType.Leaf,
                                };
                            }
                            childPaths.push(child.path);
                        }
                        workingTree[result.path] = { ...entry, children: childPaths };
                        allResults.push(result);
                        anyNew = true;
                    }

                    if (!anyNew) {
                        break;
                    }
                }

                if (signal.aborted || allResults.length === 0) {
                    return;
                }

                // 4. Apply all results to the real tree in one update.
                setTreeRef.current((tree) => {
                    const newTree = { ...tree };
                    let changed = false;

                    for (const result of allResults) {
                        const node = newTree[result.path];
                        if (!node || node.children) {
                            continue; // Already loaded by another source.
                        }

                        // Create child nodes.
                        const childPaths: string[] = [];
                        for (const child of result.children) {
                            if (!newTree[child.path]) {
                                newTree[child.path] = makeNode(child);
                            }
                            childPaths.push(child.path);
                        }

                        // Update parent with children array and persist the
                        // pagination state needed for any follow-up loads.
                        // Preserve any existing iterator when this load does
                        // not provide a new one, so truncated/aborted loads
                        // do not accidentally mark the node as fully loaded.
                        // Derive hasMoreChildren from the effective iterator
                        // (same as appendDescendants in modelTree/utils) so
                        // the two fields can never get out of sync.
                        const childIterator = result.childIterator ?? node.data.childIterator;
                        newTree[result.path] = {
                            ...node,
                            data: {
                                ...node.data,
                                childIterator,
                            },
                            children: childPaths,
                            hasMoreChildren: Boolean(childIterator),
                        };
                        changed = true;
                    }

                    return changed ? newTree : tree;
                });
            } catch {
                // Silently handle errors – the UI falls back to default
                // behavior (e.g. usePreloadDescendants / descendants arrays).
            }
        })();

        return () => abortController.abort();
        // Re-run when the tree identity changes (nodes added/removed) or when
        // the selection changes. selectedIdArr gets a new reference on every
        // selection mutation in the highlighted reducer, so it reliably
        // triggers re-runs for any selection change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree, db, selectedIdArr, fileTypes]);
}
